import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "../lib/api";
import { BallparkModal } from "../components/BallparkModal";
import { BALLPARKS } from "../lib/ballparks";
import { SearchSuggest } from "../components/SearchSuggest";
import { Thermometer, Wind, Droplets, Clock, Home, Ruler } from "lucide-react";

const ROOF_BADGE = {
  dome: { label: "Dome", cls: "bg-blue-500/20 text-blue-300 border-blue-400/40" },
  retractable: { label: "Retractable Roof", cls: "bg-purple-500/20 text-purple-300 border-purple-400/40" },
  open: { label: "Open Air", cls: "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/40" },
};

function BestGames() {
  const { data, isLoading } = useQuery({ queryKey: ["weather"], queryFn: () => api.get("/weather").then((r) => r.data) });
  const { data: hrData } = useQuery({ queryKey: ["hrAlerts"], queryFn: () => api.get("/hr-alerts").then((r) => r.data) });
  const games = data?.games || [];
  const parkHRs = {};
  (hrData?.park_hrs || []).forEach((p) => { parkHRs[p.game] = p.count; });
  const scored = games.map((g) => {
    const wx = g.weather || {};
    let s = g.park_rating || 5;
    const why = [`Park ${g.park_rating}/10`];
    if (wx.temp >= 85) { s += 2; why.push(`${wx.temp}°F — hot air carries`); }
    else if (wx.temp >= 75) { s += 1; why.push(`${wx.temp}°F warm`); }
    const wd = (wx.wind_dir || "").toLowerCase();
    if (wd.includes("out")) { s += 2; why.push(`Wind out ${wx.wind_speed} mph`); }
    else if (wd.includes("in")) { s -= 1.5; why.push("Wind blowing in"); }
    if (g.roof === "dome") why.push("Dome — weather neutral");
    if ((wx.precip || 0) >= 50) { s -= 1; why.push(`${wx.precip}% rain risk`); }
    return { ...g, bet_score: Math.round(s * 10) / 10, why, live_hrs: parkHRs[`${g.away} @ ${g.home}`] || 0 };
  }).sort((a, b) => b.bet_score - a.bet_score);
  const maxS = Math.max(...scored.map((g) => g.bet_score), 1);

  if (isLoading) return <div className="text-neutral-500 font-data text-sm animate-pulse">Scoring today's slate...</div>;
  if (scored.length === 0) return <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="bestgames-empty">No games today — rankings load on game days.</div>;
  return (
    <div className="card-surface rounded-xl p-4 sm:p-5 fade-up-1" data-testid="best-games">
      <p className="text-xs text-neutral-500 mb-4">Today's slate ranked best to worst for HR and over bets — park power + heat + wind, with live HR counts once games start.</p>
      <div className="space-y-2">
        {scored.map((g, i) => (
          <div key={i} data-testid={`bestgame-${i}`}
            className={`rounded-lg border p-3 transition-colors duration-200 ${i === 0 ? "border-[#a855f7]/60 bg-[#a855f7]/5 gold-glow" : i < 3 ? "border-[#a855f7]/30" : "border-white/5 hover:border-white/15"}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`font-data text-lg font-black w-7 text-center ${i < 3 ? "text-[#a855f7]" : "text-neutral-600"}`}>{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">
                  {g.away} <span className="text-neutral-500">@</span> {g.home}
                  {i === 0 && <span className="ml-2 text-[9px] uppercase tracking-widest font-black bg-[#a855f7] text-black px-2 py-0.5 rounded-full align-middle" data-testid="best-game-badge">Best game to bet</span>}
                </div>
                <div className="text-[10px] text-neutral-500 truncate">{g.venue} · {g.time_et}</div>
              </div>
              {g.live_hrs > 0 && (
                <span className="text-[10px] uppercase tracking-widest font-black text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/50 px-2 py-1 rounded-full animate-pulse" data-testid={`bestgame-livehr-${i}`}>
                  {g.live_hrs} HR today
                </span>
              )}
              <span className="font-data text-base font-black text-[#a855f7] w-12 text-right">{g.bet_score}</span>
            </div>
            <div className="hidden sm:block h-1.5 bg-white/10 rounded-full overflow-hidden mt-2 ml-9">
              <div className={`h-full rounded-full ${i === 0 ? "bg-[#22c55e]" : "bg-[#a855f7]"}`} style={{ width: `${(g.bet_score / maxS) * 100}%` }} />
            </div>
            <div className="flex flex-wrap gap-1 mt-2 ml-9">
              {g.why.map((w) => (
                <span key={w} className="text-[10px] font-data px-1.5 py-0.5 rounded border border-white/10 text-neutral-400">{w}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Weather() {
  const { data, isLoading } = useQuery({ queryKey: ["weather"], queryFn: () => api.get("/weather").then((r) => r.data) });
  const { data: hrData } = useQuery({ queryKey: ["hrAlerts"], queryFn: () => api.get("/hr-alerts").then((r) => r.data) });
  const allGames = data?.games || [];
  const parkHRs = {};
  (hrData?.park_hrs || []).forEach((p) => { parkHRs[p.game] = p.count; });
  const maxHRs = Math.max(0, ...Object.values(parkHRs));
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("slate");
  const [q, setQ] = useState("");
  const games = q.trim()
    ? allGames.filter((g) => `${g.away} ${g.home} ${g.venue}`.toLowerCase().includes(q.trim().toLowerCase()))
    : allGames;

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Ballpark <span className="gold-text">Weather</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Game-time forecasts for today's slate — {data?.date}. Tap any ballpark to see its dimensions and run the HR trajectory calculator.</p>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => setTab("slate")} data-testid="weather-tab-slate"
            className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border transition-colors duration-200 ${tab === "slate" ? "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/40" : "text-neutral-400 border-white/15 hover:border-[#a855f7] hover:text-[#a855f7]"}`}>
            Today's Slate
          </button>
          <button onClick={() => setTab("rankings")} data-testid="weather-tab-rankings"
            className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border transition-colors duration-200 ${tab === "rankings" ? "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/40" : "text-neutral-400 border-white/15 hover:border-[#a855f7] hover:text-[#a855f7]"}`}>
            Best Games Today
          </button>
        </div>
      </div>

      {tab === "rankings" ? (
        <BestGames />
      ) : (
        <>
          <div className="fade-up-1">
            <SearchSuggest value={q} onChange={setQ} testid="weather-search-input"
              options={[...new Set(allGames.flatMap((g) => [g.away, g.home, g.venue]))]}
              placeholder="Search a team or ballpark..." />
          </div>

          {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling today's slate and forecasts...</div>}

          {!isLoading && allGames.length === 0 && (
            <div className="card-surface rounded-xl p-10 text-center" data-testid="weather-empty">
              <p className="text-neutral-400">No MLB games scheduled today. Check back on the next game day.</p>
            </div>
          )}
          {!isLoading && q.trim() && games.length === 0 && allGames.length > 0 && (
            <p className="text-neutral-500 text-sm" data-testid="weather-search-empty">No game matches "{q}" on today's slate.</p>
          )}

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 fade-up-1">
            {games.map((g, i) => {
              const badge = ROOF_BADGE[g.roof] || ROOF_BADGE.open;
              const wx = g.weather;
              const hrs = parkHRs[`${g.away} @ ${g.home}`] || 0;
              const hottest = hrs > 0 && hrs === maxHRs;
              return (
                <div key={i} onClick={() => BALLPARKS[g.home] && setSelected(g)} role="button"
                  className={`card-surface rounded-xl overflow-hidden transition-colors duration-200 cursor-pointer hover-lift ${hottest ? "border-[#a855f7] gold-glow glow-pulse" : hrs > 0 ? "border-[#22c55e]/50" : "hover:border-[#a855f7]/40"}`} data-testid={`weather-card-${i}`}>
                  <div className="relative h-32">
                    <img src={g.image} alt="Ballpark (illustrative)" className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-black/30 to-transparent" />
                    <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${badge.cls}`}>{badge.label}</span>
                    <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border border-[#a855f7]/50 bg-black/60 text-[#a855f7]">HR Park {g.park_rating}/10</span>
                    {hrs > 0 && (
                      <span className={`absolute bottom-10 right-2 text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded-full border animate-pulse ${hottest ? "bg-[#a855f7] text-black border-[#a855f7]" : "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/60"}`} data-testid={`weather-hr-badge-${i}`}>
                        {hottest ? `Hottest park — ${hrs} HR today` : `${hrs} HR today`}
                      </span>
                    )}
                    <div className="absolute bottom-2 left-3 right-3">
                      <div className="font-display text-lg font-bold leading-tight">{g.away} <span className="text-neutral-400 text-sm">@</span> {g.home}</div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between text-xs text-neutral-400">
                      <span className="flex items-center gap-1.5"><Home size={12} className="text-[#a855f7]" />{g.venue}</span>
                      <span className="flex items-center gap-1.5"><Clock size={12} className="text-[#a855f7]" />{g.time_et}</span>
                    </div>
                    {wx ? (
                      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                        <div className="bg-black/40 border border-white/10 rounded p-2">
                          <Thermometer size={13} className="mx-auto text-[#a855f7]" />
                          <div className="font-data text-lg font-bold mt-1">{wx.temp}°F</div>
                          <div className="text-[10px] text-neutral-500">{wx.conditions}</div>
                        </div>
                        <div className="bg-black/40 border border-white/10 rounded p-2">
                          <Wind size={13} className="mx-auto text-[#a855f7]" />
                          <div className="font-data text-lg font-bold mt-1">{wx.wind_speed}<span className="text-xs text-neutral-500"> mph</span></div>
                          <div className="text-[10px] text-neutral-500">{wx.wind_dir}</div>
                        </div>
                        <div className="bg-black/40 border border-white/10 rounded p-2">
                          <Droplets size={13} className="mx-auto text-[#a855f7]" />
                          <div className="font-data text-lg font-bold mt-1">{wx.precip}%</div>
                          <div className="text-[10px] text-neutral-500">Rain</div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 text-xs text-neutral-500">Forecast unavailable for this venue.</div>
                    )}
                    <p className="text-xs text-neutral-400 mt-3 italic border-t border-white/10 pt-2">{g.note}</p>
                    {BALLPARKS[g.home] && (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#a855f7]" data-testid={`ballpark-open-${i}`}>
                        <Ruler size={12} /> View park & HR trajectory →
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-neutral-600">Stadium photos are illustrative (Unsplash, free license). Forecasts by Open-Meteo; schedule via MLB Stats API.</p>
        </>
      )}
      {selected && <BallparkModal game={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
